import {
  Container,
  Paper,
  Text,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Badge,
  Stack,
} from "@mantine/core";
import { IconPlus, IconSearch, IconEye, IconEdit } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useRFQs } from "../hooks/useRFQ";
import type { RFQ } from "../../../api/rfq";
import { useAuth } from "../../../auth/AuthContext";
import dayjs from "dayjs";

const statusColors: Record<string, string> = {
  DRAFT: "gray",
  PENDING_APPROVAL: "yellow",
  APPROVED: "green",
  SENT: "blue",
  QUOTES_RECEIVED: "cyan",
  UNDER_REVIEW: "yellow",
  AWARDED: "green",
  CANCELLED: "red",
  EXPIRED: "orange",
};

export function RFQList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<{
    search?: string;
    status?: RFQ["status"];
    category?: RFQ["category"];
  }>({
    search: "",
    status: (searchParams.get("status") as RFQ["status"]) || undefined,
    category: undefined,
  });

  const { data: rfqs, isLoading } = useRFQs(filters);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading RFQs...</Text>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>RFQs</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate("/rfq/new")}
        >
          New RFQ
        </Button>
      </Group>

      <Paper shadow="sm" p="md" mb="xl">
        <Group gap="md">
          <TextInput
            placeholder="Search RFQs..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={[
              { value: "", label: "All Statuses" },
              { value: "DRAFT", label: "Draft" },
              { value: "PENDING_APPROVAL", label: "Pending Approval" },
              { value: "APPROVED", label: "Approved" },
              { value: "SENT", label: "Sent" },
              { value: "QUOTES_RECEIVED", label: "Quotes Received" },
              { value: "UNDER_REVIEW", label: "Under Review" },
              { value: "AWARDED", label: "Awarded" },
              { value: "CANCELLED", label: "Cancelled" },
              { value: "EXPIRED", label: "Expired" },
            ]}
            value={filters.status || ""}
            onChange={(value) => handleFilterChange("status", value || "")}
            style={{ minWidth: 180 }}
          />
          <Select
            placeholder="Category"
            data={[
              { value: "", label: "All Categories" },
              { value: "GOODS", label: "Goods" },
              { value: "SERVICES", label: "Services" },
            ]}
            value={filters.category || ""}
            onChange={(value) => handleFilterChange("category", value || "")}
            style={{ minWidth: 150 }}
          />
        </Group>
      </Paper>

      <Paper shadow="sm" p="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>RFQ Number</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Deadline</Table.Th>
              <Table.Th>Vendors</Table.Th>
              <Table.Th>Quotations</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rfqs?.map((rfq) => (
              <Table.Tr key={rfq.id}>
                <Table.Td>{rfq.rfqNumber}</Table.Td>
                <Table.Td>{rfq.title}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{rfq.category}</Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusColors[rfq.status]} variant="light">
                    {rfq.status.replace("_", " ")}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {dayjs(rfq.deadline).format("DD MMM YYYY")}
                </Table.Td>
                <Table.Td>{rfq.vendors?.length || 0}</Table.Td>
                <Table.Td>{rfq.quotations?.length || 0}</Table.Td>
                <Table.Td>
                  {dayjs(rfq.createdAt).format("DD MMM YYYY")}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => navigate(`/rfq/${rfq.id}`)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                    {rfq.status === "DRAFT" &&
                      (rfq.createdBy.id === user?.id ||
                        user?.role === "ADMIN" ||
                        user?.role === "PROCUREMENT_OFFICER" ||
                        user?.role === "HEAD_OF_PROCUREMENT") && (
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => navigate(`/rfq/${rfq.id}/edit`)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {(!rfqs || rfqs.length === 0) && (
          <Stack align="center" py="xl">
            <Text c="dimmed">No RFQs found</Text>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

